import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { distinctUntilChanged, finalize, of, pipe, switchMap } from 'rxjs';
import { CaseService } from '../../shared/services/case.service';
import { tapResponse } from '@ngrx/operators';
import { PhoneDto } from '../../shared/Models/PhoneDto';
import { Router } from '@angular/router';
import { FormService } from '../../shared/services/form.service';
import { PostRequestCallbackRequest } from '../../shared/Models/Requests/PostRequestCallbackRequest';
import { CaseDto } from '../../shared/Models/CaseDto';
import { UserService } from '../../shared/services/user.service';
import { DictionaryService } from '../../shared/services/dictionary.service';
import { AppParametersDto } from '../../shared/Models/AppParametersDto';
import { NotificationService } from '../../shared/services/notification.service';
import { LoadingSpinnerService } from '../../shared/services/loading-spinner-service';
import { Params } from '../../shared/Enums/Params.enum';

export interface ContactFormData {
  caseNumber: string,
  phoneNumber: PhoneDto,
  checked: boolean,
  date: Date,
  hour: Date,
}

interface OrderContactState {
  formStep: number,
  form: FormGroup | null
  cases: CaseDto[],
  phoneNumbers: PhoneDto[],
  contactFormData: ContactFormData | null,
  appParams: AppParametersDto [] | null,
  isError: boolean,
  isLoading: boolean,
  isCasesLoading: boolean,
  isPhonesLoading: boolean,
}

const orderContactDetailsState: OrderContactState = {
  formStep: 0,
  form: null,
  cases: [],
  phoneNumbers: [],
  contactFormData: null,
  appParams: null,
  isError: false,
  isLoading: false,
  isCasesLoading: false,
  isPhonesLoading: false,
};

const dateFieldsValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const date = control.get('date');
  const hour = control.get('hour');
  const checked = control.get('checked');
  return (date?.value && hour?.value) || checked?.value
    ? null
    : { wrongData: true };
};

export const OrderContactStore = signalStore(
  { providedIn: 'root' },
  withState(orderContactDetailsState),
  withProps(() => ({
    formBuilder: inject(FormBuilder),
    caseService: inject(CaseService),
    userService: inject(UserService),
    formService: inject(FormService),
    router: inject(Router),
    dictionaryService: inject(DictionaryService),
    notificationService: inject(NotificationService),
    loaderService: inject(LoadingSpinnerService),
  })),
  withComputed(({ contactFormData, appParams, cases }) => ({
    getMinOpenHours: computed(() => {
      const params = appParams();
      return params?.find(x => x.name === Params.MinCallBackTime)?.value;
    }),
    getMaxOpenHours: computed(() => {
      const max = appParams();
      return max?.find(x => x.name === Params.MaxCallBackTime)?.value;
    }),
    getMaxCallbackDays: computed(() => {
      const params = appParams();
      const maxCallbackDays = params?.find(x => x.name === Params.MaxCallBackDays)?.value;
      let constraint = 7;
      if(maxCallbackDays)
      {
        constraint = Number(maxCallbackDays);
      }
      const resultDate = new Date();
      resultDate.setDate(new Date().getDate() + constraint);
      return resultDate;
    }),
    confirmation: computed(() => {
      const data = contactFormData();
      if (data?.checked) {
        const date = new Date();
        date.setMinutes(date.getMinutes() + 10);
        return date;
      }

      const date = new Date(data?.date || new Date());
      date.setHours(data?.hour.getHours() || 0);
      date.setMinutes(data?.hour.getMinutes() || 0);
      return date;

    }),
    confirmationDate: computed(() => {
      const data = contactFormData();
      if (data?.checked) {
        const date = new Date();
        date.setMinutes(date.getMinutes() + 10);
        return date;
      }

      if(data) {
        return new Date(
          data?.date?.getFullYear(),
          data?.date.getMonth(),
          data?.date.getDate(),
          data?.hour.getHours(),
          data?.hour.getMinutes(),
        );
      }
      return new Date();
    }),
    caseDto: computed(() => {
      const data = contactFormData();
      const caseValues = cases();
      return caseValues.find(x => x.caseIdHash === data?.caseNumber);
    }),
  })),
  withComputed(store => ({
    confirmationDateText: computed(() => {
      const date = store.confirmationDate();
      const dayPart = date?.toLocaleString('pl-PL', { day: 'numeric', month: '2-digit', year: 'numeric' });
      const hourPart = date?.toLocaleString('pl-PL', { hour: 'numeric', minute: 'numeric' });
      return `${dayPart} - ${hourPart}`;
    }),
  })),
  withMethods(({ formBuilder, caseService, userService, formService, router, dictionaryService, notificationService, loaderService, ...store }) => ({
    setup(): void {
      patchState(store, () => ({
        form: formBuilder.group({
          caseNumber: ['', Validators.required],
          phoneNumber: ['', Validators.required],
          date: [''],
          hour: [{ value: '', disabled: true }],
          checked: [false],
          isError: false,
        }, { validators: dateFieldsValidator }),
      }));
    },
    nextStep(): void {
      patchState(store, (state) => ({
        formStep: state.formStep += 1,
      }));
    },
    previousStep(): void {
      patchState(store, (state) => ({
        formStep: state.formStep -= 1,
      }));
    },
    navigateToDashboard(): void {
      router.navigate(['']);
    },
    loadDateForConfirmation(contact: ContactFormData): void {
      patchState(store, () => ({
        contactFormData: contact,
      }));
    },
    loadPhoneNumbers: rxMethod<void>(
      pipe(
        switchMap(() => {
          patchState(store, { isPhonesLoading: true });
          return userService.getPhoneNumbers().pipe(
            tapResponse({
              next: (phoneResult) => patchState(store, { phoneNumbers: phoneResult.phones, isPhonesLoading: false }),
              error: () => patchState(store, {
                isError: true,
                isPhonesLoading: false,
              }),
            }),
          );
        }),
      ),
    ),
    loadCasesId: rxMethod<void>(
      pipe(
        distinctUntilChanged(),
        switchMap(() => {
          patchState(store, { isCasesLoading: true });
          return caseService.getCases().pipe(
            tapResponse({
              next: (caseResult) => patchState(store, { cases: caseResult.caseDto,
                isCasesLoading: false }),
              error: () => patchState(store, {
                isError: true,
                isCasesLoading: false,
              }),
            }),
          );
        }),
      ),
    ),
    getParams: rxMethod<void>(
      pipe(
        distinctUntilChanged(),
        switchMap(() => {
          loaderService.show();
          return dictionaryService.getAppParams().pipe(
            finalize(() => loaderService.hide()),
            tapResponse({
              next: (appParamsResult) => patchState(store, { appParams: appParamsResult.params }),
              error: () => patchState(store, {
                isError: true,
              }),
            }),
          );
        }),
      ),
    ),
    submitOrder: rxMethod<void>(
      pipe(
        switchMap(() => {
          patchState(store, { isLoading: true });
          const data = store.contactFormData();
          const contactDate = store.confirmationDate();
          if(data && contactDate) {
            const requestData = {
              caseIdHash: data?.caseNumber,
              callbackDate: contactDate,
              phoneIdHash: data?.phoneNumber.phoneIdHash,
            } as PostRequestCallbackRequest;
            return formService.requestCallback(requestData).pipe(
              tapResponse({
                next: () => patchState(store, (state) => ({
                  formStep: state.formStep += 1,
                  isLoading: false,
                })),
                error: () => {
                  notificationService.error('notification.error');
                  patchState(store, { isLoading: false });
                  return;
                },
              }),
            );
          }
          patchState(store, { isLoading: false });
          return of();
        },
        ),
      ),
    ),
    init(): void {
      this.setup();
      this.loadPhoneNumbers();
      this.loadCasesId();
      this.getParams();
    },
  })),
  withHooks({
    onInit(store) {
      store.init();
    },
  }),
);